import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const channelAdapter = createEntityAdapter({
  selectId: (channel) => {
    if (!channel) return "default";
    return channel._id || channel.id || "default";
  },
});

// Async Thunks
export const updateChannel = createAsyncThunk(
  "channel/update",
  async (channelData, { rejectWithValue }) => {
    try {
      const response = await request({
        endpoint: "/user/updateChannel",
        method: "PUT",
        body: channelData,
      });

      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error.message || "Failed to update channel."
        );
      }

      return response.data;
    } catch ({ error }) {
      return rejectWithValue(
        error.message ||
        "An unexpected error occurred while updating the channel."
      );
    }
  }
);

// Initial state
const initialState = channelAdapter.getInitialState({
  loading: false,
  error: null,
});

// Slice
const channelSlice = createSlice({
  name: "channel",
  initialState,
  reducers: {
    addChannel: (state, action) => {
      if (!action.payload) return;
      channelAdapter.upsertMany(state, action.payload);
    },
    clearChannel: (state) => {
      channelAdapter.removeAll(state);
      state.loading = false;
      state.error = null;
    },
    updateChannelIO: (state, action) => {
      channelAdapter.upsertOne(state, action.payload);
    },
    removeChannel: (state, action) => {
      channelAdapter.removeOne(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChannel.fulfilled, (state, action) => {
        channelAdapter.upsertOne(state, action.payload);
        state.loading = false;
      })
      .addCase(updateChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
export const {
  selectAll: selectAllChannels,
  selectById: selectChannelById,
  selectIds: selectChannelIds,
} = channelAdapter.getSelectors((state) => state.channel);

// Actions & Reducer Export
export const { clearChannel, addChannel, updateChannelIO, removeChannel } =
  channelSlice.actions;
export default channelSlice.reducer;
